import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import ManageHistoryRoundedIcon from "@mui/icons-material/ManageHistoryRounded";
import { Alert, Box, Button, Divider, Paper, Stack, Tab, Tabs, Typography } from "@mui/material";
import { OssStatusChip } from "../OssStatusChip";
import {
  actionStatus,
  actionType,
  displayLabel,
  historyDomain,
  orderStatus,
  riskLevel,
  scheduleStatus,
  sourceType,
} from "../../constants/labels";
import { getLineActionReason, isActionNeeded, panelSx } from "./detailUtils";
import { InfoItem, MetricCard } from "./OssDetailPrimitives";
import { OssDetailChangeFlow, OssDetailDocumentFlow } from "./OssDetailFlowSections";

const labelSources = [orderStatus, scheduleStatus, actionType, actionStatus, sourceType];

const toDisplayValue = (value) => {
  if (!value) return "-";
  for (const source of labelSources) {
    const label = displayLabel(source, value);
    if (label !== value) return label;
  }
  return value;
};

const formatHistoryDescription = (description) => {
  if (!description) return "-";
  return String(description)
    .split(/\s*->\s*/)
    .map((part) => (part === "신규" ? part : toDisplayValue(part)))
    .join(" -> ");
};

export const OssOrderSummary = ({ order, summary, compact = false }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: compact ? "1fr" : { xs: "1fr", lg: "minmax(0, 1.65fr) minmax(360px, 0.95fr)" },
      gap: 1.5,
      alignItems: "stretch",
    }}
  >
    <Paper elevation={0} sx={{ ...panelSx, p: 2, minHeight: 184 }}>
      <Typography sx={{ fontSize: 17, fontWeight: 800, color: "#111827" }}>주문 정보</Typography>
      <Box
        sx={{
          mt: 1.4,
          display: "grid",
          gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: compact ? "repeat(3, minmax(0, 1fr))" : "repeat(4, minmax(0, 1fr))" },
          gap: 1.6,
          alignItems: "start",
        }}
      >
        <InfoItem label="고객 발주번호" value={order.customer_po_no} />
        <InfoItem label="주문일자" value={order.order_date} />
        <InfoItem label="입력 방식" value={displayLabel(sourceType, order.source_type)} />
        <InfoItem label="최근 수정" value={order.updated_at} />
        <InfoItem label="담당자 키" value={order.manager_user_key} />
        <InfoItem label="비고" value={order.note} />
      </Box>
    </Paper>

    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 1, alignContent: "stretch" }}>
      <MetricCard label="관리 품목" value={`${summary.lineCount}건`} helper="주문 품목 기준" />
      <MetricCard label="조치 필요" value={`${summary.actionCount}건`} tone={summary.actionCount ? "#ef4444" : "#14b8a6"} helper="처리 후 목록에서 제외" />
      <MetricCard label="가장 빠른 착수일" value={summary.earliestStart} tone="#2563eb" />
      <MetricCard label="최소 여유일" value={`${summary.minMargin}일`} tone={Number(summary.minMargin) < 0 ? "#ef4444" : "#f59e0b"} helper={`확인일 ${summary.earliestCheck || "-"}`} />
    </Box>
  </Box>
);

export const OssOrderLineCards = ({ lines, disabledByCancel, onAction }) => (
  <Stack spacing={1.2} sx={{ p: 1.5 }}>
    {lines.map((line) => {
      const riskMeta = riskLevel[line.risk_level] || riskLevel.NORMAL;
      const actionNeeded = isActionNeeded(line) && !disabledByCancel;
      const canProcessAction = actionNeeded && typeof onAction === "function";
      return (
        <Box
          key={line.order_line_id}
          sx={{
            p: 1.6,
            border: "1px solid #e5e7eb",
            borderLeft: `5px solid ${riskMeta.tone}`,
            borderRadius: "8px",
            bgcolor: "#fff",
          }}
        >
          <Stack direction={{ xs: "column", md: "row" }} spacing={1.3} alignItems={{ md: "center" }} justifyContent="space-between">
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: 16.5, fontWeight: 800, color: "#111827" }}>
                {line.item_name_snapshot}
              </Typography>
              <Typography sx={{ mt: 0.25, fontSize: 12.5, color: "#64748b" }}>
                {line.item_code_snapshot || "품목코드 미등록"} · {line.specification_snapshot || "규격 미등록"}
              </Typography>
            </Box>
            <Stack direction="row" spacing={0.7} alignItems="center" flexWrap="wrap">
              <OssStatusChip value={line.schedule_status} />
              <OssStatusChip type="risk" value={line.risk_level} />
              {canProcessAction && (
                <Button size="small" variant="contained" startIcon={<FactCheckOutlinedIcon />} onClick={() => onAction(line)}>
                  조치 처리
                </Button>
              )}
            </Stack>
          </Stack>

          <Divider sx={{ my: 1.4 }} />

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, 1fr)", lg: "repeat(6, 1fr)" }, gap: 1.4 }}>
            <InfoItem label="수량" value={`${Number(line.order_quantity || 0).toLocaleString()} ${line.unit_code || ""}`} strong />
            <InfoItem label="요청 납기" value={line.requested_due_date} strong />
            <InfoItem label="착수 필요일" value={line.required_start_date} strong />
            <InfoItem label="확인 필요일" value={line.check_required_date} />
            <InfoItem label="소요일/여유일" value={`${line.standard_lead_days ?? "-"}일 / ${line.planning_buffer_days ?? "-"}일`} />
            <InfoItem label="남은 여유일" value={line.available_margin_days ?? "-"} strong={Number(line.available_margin_days) <= 7} />
          </Box>

          {actionNeeded && (
            <Alert severity={line.risk_level === "OVERDUE" || line.risk_level === "HIGH" ? "error" : "warning"} sx={{ mt: 1.4 }}>
              {getLineActionReason(line, riskLevel, scheduleStatus)} 항목입니다. 처리 완료 시 우선 조치 목록에서 제외됩니다.
            </Alert>
          )}
        </Box>
      );
    })}
  </Stack>
);

export const OssOrderDetailTabs = ({
  tab,
  onTabChange,
  lines,
  data,
  changes,
  changesLoading,
  disabledByCancel,
  onAction,
  onDocumentUpload,
  onDocumentOpen,
  onDocumentDelete,
  onChangeRequest,
  onReviewChange,
}) => (
  <Paper elevation={0} sx={{ ...panelSx, overflow: "hidden" }}>
    <Tabs value={tab} onChange={(_, value) => onTabChange(value)} sx={{ px: 1.5, borderBottom: "1px solid #e5e7eb" }}>
      <Tab icon={<Inventory2OutlinedIcon />} iconPosition="start" label="품목/일정" />
      <Tab icon={<DescriptionOutlinedIcon />} iconPosition="start" label="문서" />
      <Tab icon={<ManageHistoryRoundedIcon />} iconPosition="start" label="변경 검토" />
      <Tab icon={<HistoryRoundedIcon />} iconPosition="start" label="이력" />
    </Tabs>

    {tab === 0 && <OssOrderLineCards lines={lines} disabledByCancel={disabledByCancel} onAction={onAction} />}

    {tab === 1 && (
      <OssDetailDocumentFlow
        documents={data?.documents || []}
        disabled={disabledByCancel}
        onUpload={onDocumentUpload}
        onOpen={onDocumentOpen}
        onDelete={onDocumentDelete}
      />
    )}

    {tab === 2 && (
      <OssDetailChangeFlow
        changes={changes}
        lines={lines}
        loading={changesLoading}
        disabled={disabledByCancel}
        onRequest={onChangeRequest}
        onReview={onReviewChange}
      />
    )}

    {tab === 3 && (
      <Stack spacing={1} sx={{ p: 1.5 }}>
        {(data?.actions || []).length > 0 && (
          <Box sx={{ mb: 0.4 }}>
            <Typography sx={{ mb: 0.8, fontSize: 15, fontWeight: 800, color: "#111827" }}>조치 이력</Typography>
            <Stack spacing={0.8}>
              {data.actions.map((action) => (
                <Box key={action.action_id} sx={{ p: 1.2, border: "1px solid #e5e7eb", borderRadius: "8px", bgcolor: "#fbfbfe" }}>
                  <Typography sx={{ fontWeight: 800 }}>{action.action_title || actionType[action.action_type] || action.action_type}</Typography>
                  <Typography sx={{ mt: 0.25, fontSize: 12.5, color: "#64748b" }}>
                    {displayLabel(actionStatus, action.action_status)} · 기한 {action.due_at || "-"}
                  </Typography>
                  {action.result_note && <Typography sx={{ mt: 0.35, fontSize: 13, color: "#374151" }}>{action.result_note}</Typography>}
                </Box>
              ))}
            </Stack>
          </Box>
        )}

        <Typography sx={{ mb: 0.4, fontSize: 15, fontWeight: 800, color: "#111827" }}>상태 이력</Typography>
        {(data?.histories || []).map((history, index) => (
          <Box key={`${history.occurred_at}-${index}`} sx={{ p: 1.2, border: "1px solid #e5e7eb", borderRadius: "8px" }}>
            <Typography sx={{ fontSize: 13, color: "#64748b" }}>{history.occurred_at}</Typography>
            <Typography sx={{ mt: 0.3, fontWeight: 800 }}>
              {displayLabel(historyDomain, history.title)} · {formatHistoryDescription(history.description)}
            </Typography>
            {history.note && <Typography sx={{ mt: 0.25, fontSize: 13, color: "#64748b" }}>{history.note}</Typography>}
          </Box>
        ))}
      </Stack>
    )}
  </Paper>
);
